import React from 'react';
import {
  Button,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  HStack,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import type { StreamServer } from '../types';
import { streamOutlineOnDarkHover } from '../darkThemeButtonProps';

interface Props {
  activeLabel: string;
  servers: StreamServer[];
  activeServerId: string | null;
  onSelectServer: (id: string) => void;
  onAddServer: () => void;
  onManageServers: () => void;
  loading?: boolean;
}

export function ServerSwitcher({
  activeLabel,
  servers,
  activeServerId,
  onSelectServer,
  onAddServer,
  onManageServers,
  loading,
}: Props) {
  const compact = useBreakpointValue({ base: true, md: false });

  return (
    <Menu placement="bottom-end" strategy="fixed">
      <MenuButton
        as={Button}
        size="sm"
        variant="outline"
        borderColor="whiteAlpha.300"
        color="white"
        {...streamOutlineOnDarkHover}
        isLoading={loading}
        maxW={{ base: '140px', md: '220px' }}
        fontWeight="600"
      >
        <HStack w="100%" spacing={2} justify="space-between" align="center" color="inherit">
          <Text
            as="span"
            noOfLines={1}
            flex={1}
            minW={0}
            textAlign="center"
            fontWeight="600"
            lineHeight="1.25"
            color="inherit"
          >
            {compact ? activeLabel.slice(0, 12) : activeLabel}
          </Text>
          <ChevronDownIcon boxSize={4} flexShrink={0} aria-hidden color="inherit" />
        </HStack>
      </MenuButton>
      <MenuList
        bg="#1a222c"
        borderColor="whiteAlpha.200"
        minW="240px"
        py={1}
        zIndex={2000}
      >
        {servers.map((s) => {
          const active = activeServerId === s.id;
          return (
            <MenuItem
              key={s.id}
              bg="transparent"
              color="white"
              _hover={{ bg: 'whiteAlpha.100' }}
              onClick={() => onSelectServer(s.id)}
              py={2}
            >
              <VStack align="stretch" spacing={0} w="full">
                <HStack justify="space-between" spacing={2}>
                  <Text flex={1} noOfLines={1} fontWeight={active ? '700' : '500'}>
                    {s.name}
                  </Text>
                  {active && (
                    <Text fontSize="10px" color="teal.300" flexShrink={0}>
                      Active
                    </Text>
                  )}
                </HStack>
                <Text fontSize="xs" color="whiteAlpha.500" noOfLines={1}>
                  {s.server} · {s.username}
                </Text>
              </VStack>
            </MenuItem>
          );
        })}
        {servers.length > 0 && <MenuDivider borderColor="whiteAlpha.200" />}
        <MenuItem bg="transparent" color="teal.200" _hover={{ bg: 'whiteAlpha.100' }} onClick={onAddServer}>
          + Add server
        </MenuItem>
        <MenuItem bg="transparent" color="whiteAlpha.800" _hover={{ bg: 'whiteAlpha.100' }} onClick={onManageServers}>
          Manage servers
        </MenuItem>
      </MenuList>
    </Menu>
  );
}
